import { ASSET_TYPE_ID } from "../../const";
import { BASIC_PAGE, EXIT_PAGE, TITLE_PAGE, QUIZ_PAGE, PageTypes } from "../../components/constants/pageTypes";
import IAsset from "../../models/IAsset";
import { fetchSmartObjectJson } from "../../lib/SmartObject/upload";
import { compareSmartObjectsVersions } from "../../lib/SmartObject";

// IMAGE | BASE
function updateBaseImageAsset(pageManifest: any, oldAssetData: IAsset, newAssetData: IAsset) {
  const manifestCopy = JSON.parse(JSON.stringify(pageManifest));
  if (manifestCopy.pageImage && Array.isArray(manifestCopy.pageImage)) {
    manifestCopy.pageImage = manifestCopy.pageImage.map((img: any) => {
      if (String(img.imagePath) === String(oldAssetData.blobPath)) {
        return {
          ...img,
          imagePath: newAssetData.blobPath,
          assetVersionId: newAssetData.assetVersionId,
        };
      }
      return img;
    });
  }
  return manifestCopy;
}

// IMAGE | TITLE and EXIT
function updateOverlayLogoImageAsset(pageManifest: any, oldAssetData: IAsset, newAssetData: IAsset) {
  const manifestCopy = JSON.parse(JSON.stringify(pageManifest));
  // AirplaneOverlay
  if (
    manifestCopy.AirplaneOverlay &&
    String(manifestCopy.AirplaneOverlay.imagePath) === String(oldAssetData.blobPath)
  ) {
    manifestCopy.AirplaneOverlay.imagePath = newAssetData.blobPath;
    manifestCopy.AirplaneOverlay.assetVersionId = newAssetData.assetVersionId;
  }
  // BrandedLogo
  if (manifestCopy.BrandedLogo && String(manifestCopy.BrandedLogo.imagePath) === String(oldAssetData.blobPath)) {
    manifestCopy.BrandedLogo.imagePath = newAssetData.blobPath;
    manifestCopy.BrandedLogo.assetVersionId = newAssetData.assetVersionId;
  }
  // Background
  if (manifestCopy.Background && String(manifestCopy.Background.imagePath) === String(oldAssetData.blobPath)) {
    manifestCopy.Background.imagePath = newAssetData.blobPath;
    manifestCopy.Background.assetVersionId = newAssetData.assetVersionId;
  }
  return manifestCopy;
}

// IMAGE | QUIZ
function updateQuizImageAsset(pageManifest: any, oldAssetData: IAsset, newAssetData: IAsset) {
  const manifestCopy = JSON.parse(JSON.stringify(pageManifest));
  if (manifestCopy.image && Array.isArray(manifestCopy.image)) {
    manifestCopy.image = manifestCopy.image.map((img: any) => {
      if (String(img.imagePath) === String(oldAssetData.blobPath)) {
        return {
          ...img,
          imagePath: newAssetData.blobPath,
          assetVersionId: newAssetData.assetVersionId,
        };
      }
      return img;
    });
  }
  return manifestCopy;
}

// SMART_OBJECT
async function updateSmartObjectAsset(pageManifest: any, oldAssetData: IAsset, newAssetData: IAsset) {
  const manifestCopy = JSON.parse(JSON.stringify(pageManifest));
  const metaVariables = manifestCopy.metaVariables;
  const metaVariablesData = manifestCopy.metaVariablesData;
  const animatedMetaVariables = manifestCopy.animatedMetaVariables;
  const newSmartObjects: any[] = [];

  if (manifestCopy.smartObjects && Array.isArray(manifestCopy.smartObjects)) {
    for (const obj of manifestCopy.smartObjects) {
      if (obj.assetVersionId === oldAssetData.assetVersionId) {
        const id = obj.objectId;
        const [oldJson, newJson] = await Promise.all([
          fetchSmartObjectJson(oldAssetData),
          fetchSmartObjectJson(newAssetData),
        ]);
        const { cleanReport } = compareSmartObjectsVersions(oldJson, newJson, id);
        if (cleanReport) {
          newSmartObjects.push({
            ...obj,
            elementList: [newJson[0]],
            assetVersionId: newAssetData.assetVersionId,
          });
        } else {
          // TODO: handle not clean report
          // we need to adjust the values of
          // metaVariables, metaVariablesData, animatedMetaVariables
          // which could mean either deleting or updating values to be valid
          newSmartObjects.push(obj);
        }
      } else {
        newSmartObjects.push(obj);
      }
    }
  }

  manifestCopy.smartObjects = newSmartObjects;

  return manifestCopy;
}

// VIDEO
function updateVideoAsset(pageManifest: any, oldAssetData: IAsset, newAssetData: IAsset) {
  const manifestCopy = JSON.parse(JSON.stringify(pageManifest));
  // Pode ser array ou objeto único
  if (manifestCopy.videos && Array.isArray(manifestCopy.videos)) {
    manifestCopy.videos = manifestCopy.videos.map((vid: any) => {
      if (String(vid.imagePath) === String(oldAssetData.blobPath)) {
        return { ...vid, ...newAssetData };
      }
      return vid;
    });
  } else if (manifestCopy.video && manifestCopy.video.imagePath) {
    if (String(manifestCopy.video.imagePath) === String(oldAssetData.blobPath)) {
      manifestCopy.video = { ...manifestCopy.video, ...newAssetData };
    }
  }
  return manifestCopy;
}

export function updatePageManifestAssetByType(
  pagePlayerType: PageTypes,
  pageManifest: any,
  oldAssetData: IAsset,
  newAssetData: IAsset,
) {
  const assetType = newAssetData.assetTypeId;

  // Flag new assets
  pageManifest.replacedAssets = pageManifest.replacedAssets || [];
  pageManifest.replacedAssets.push({
    assetVersionId: newAssetData.assetVersionId,
    assetTypeId: newAssetData.assetTypeId,
  });

  if (assetType === ASSET_TYPE_ID.IMAGE) {
    switch (pagePlayerType) {
      case BASIC_PAGE:
        return updateBaseImageAsset(pageManifest, oldAssetData, newAssetData);
      case TITLE_PAGE:
      case EXIT_PAGE:
        return updateOverlayLogoImageAsset(pageManifest, oldAssetData, newAssetData);
      case QUIZ_PAGE:
        return updateQuizImageAsset(pageManifest, oldAssetData, newAssetData);
      default:
        return pageManifest;
    }
  }
  if (assetType === ASSET_TYPE_ID.SMART_OBJECT) {
    return updateSmartObjectAsset(pageManifest, oldAssetData, newAssetData);
  }
  if (assetType === ASSET_TYPE_ID.VIDEO) {
    return updateVideoAsset(pageManifest, oldAssetData, newAssetData);
  }

  return pageManifest;
}
